import type {
  AdminApiVoiceUsage,
  VoiceCreditUsageByAgent,
} from "@repo/app-types/entities/creditUsage";
import { resolvePeriodCreditCapacity } from "@repo/app-types/entities/metronome";
import { InfoBanner } from "@repo/ui/InfoBanner/infoBanner";
import { Link } from "@repo/ui/Link/index";
import { TableSkeleton } from "@repo/ui/Skeletons/skeletons";
import { Fragment, type FC } from "react";
import {
  useContractProductsQuery,
  useVoiceUsageQuery,
} from "../../../api/entities/company.js";
import { SettingsTable } from "../shared/settingsTable.js";
import { SettingsTableBodySection } from "../shared/settingsTableBodySection.js";
import { SettingsTableDataCell } from "../shared/settingsTableDataCell.js";
import { SettingsTableHeaderCell } from "../shared/settingsTableHeaderCell.js";
import { SettingsTableHeaderSection } from "../shared/settingsTableHeaderSection.js";
import {
  CreditUsageCardSkeleton,
  CreditUsageThresholdBanner,
  projectPeriodTotal,
  UsageBar,
  useCreditUsageSection,
} from "./creditUsageSection.js";
import {
  formatPeriodLabel,
  formatPeriodResetDate,
  formatUsageAgainstIncluded,
  formatWholeNumber,
} from "./usageFormatting.js";

interface VoiceCreditsSectionProps {
  periodOffset: number;
}

/**
 * The voice agent section of the billing page: the period's voice credit
 * balance and usage per agent, for the period the page-level header selects.
 * Voice credits are their own balance, bought as voice packs, so the section
 * speaks in voice credits throughout.
 */
export const VoiceCreditsSection: FC<VoiceCreditsSectionProps> = (props) => {
  const { periodOffset } = props;
  const { selectedUsage, isUsageLoading } = useCreditUsageSection(
    useVoiceUsageQuery,
    periodOffset,
  );
  const { data: contractProducts, isLoading: isContractProductsLoading } =
    useContractProductsQuery();
  const hasVoicePack =
    contractProducts?.product_keys.includes("voice_credit_pack_subscription") ??
    false;

  const isLoading = isUsageLoading || isContractProductsLoading;
  const isCurrentPeriod = selectedUsage?.selected_period.offset === 0;
  const barCapacity = selectedUsage
    ? resolveVoiceBarCapacity(selectedUsage, hasVoicePack)
    : null;
  const agents = selectedUsage?.agents ?? [];

  return (
    <section className="space-y-6">
      <div className="space-y-3">
        <h2 className="text-base font-semibold text-foreground">
          Voice agent credits
        </h2>
        {isCurrentPeriod && selectedUsage ? (
          <>
            {!hasVoicePack && selectedUsage.credits_used > 0 ? (
              <NoVoicePackBanner />
            ) : null}
            {barCapacity !== null ? (
              <CreditUsageThresholdBanner
                percentage={(selectedUsage.credits_used / barCapacity) * 100}
                isUncovered={selectedUsage.credits_overage > 0}
              />
            ) : null}
          </>
        ) : null}
        {selectedUsage && !isLoading ? (
          <UsageOverview usage={selectedUsage} hasVoicePack={hasVoicePack} />
        ) : (
          <CreditUsageCardSkeleton />
        )}
      </div>
      {/* Only agents Metronome metered are listed, so an empty list is a
          period with no voice usage to break down. */}
      {!isUsageLoading && agents.length === 0 ? null : (
        <UsageByAgent agents={agents} isLoading={isUsageLoading} />
      )}
    </section>
  );
};

/**
 * The period's spendable voice credits, or null when there is no allowance to
 * pace against. Voice credits come only from packs.
 */
function resolveVoiceBarCapacity(
  usage: AdminApiVoiceUsage,
  hasVoicePack: boolean,
): number | null {
  if (!hasVoicePack) {
    return null;
  }
  const capacity = resolvePeriodCreditCapacity(usage);
  return capacity !== null && capacity > 0 ? capacity : null;
}

interface UsageOverviewProps {
  usage: AdminApiVoiceUsage;
  /** Whether the contract holds a voice credit pack, spent or not. */
  hasVoicePack: boolean;
}

/**
 * Voice credits are bought in packs, never included with a license, so a live
 * period paces usage against every credit still spendable and a closed one
 * reports its usage alone. Without a pack there is no allowance to pace or
 * to run out of: the card reports the usage and flags that it accrues
 * uncontracted.
 */
const UsageOverview: FC<UsageOverviewProps> = (props) => {
  const { usage, hasVoicePack } = props;

  const isCurrentPeriod = usage.selected_period.offset === 0;
  const liveBalances =
    hasVoicePack && usage.balances.kind === "live" ? usage.balances : null;
  const barCapacity = resolveVoiceBarCapacity(usage, hasVoicePack);
  const isUncovered = hasVoicePack && usage.credits_overage > 0;
  const projectedCredits = isCurrentPeriod ? projectPeriodTotal(usage) : null;

  const details = [
    formatUsageAgainstIncluded(
      usage.credits_used,
      barCapacity,
      "voice credits used",
    ),
    usage.balances.kind === "at_period_close" &&
    usage.balances.remaining_purchased_credits > 0
      ? `${formatWholeNumber(usage.balances.remaining_purchased_credits)} purchased credits left at period close`
      : null,
    isUncovered
      ? `${formatWholeNumber(usage.credits_overage)} uncovered`
      : null,
    isCurrentPeriod
      ? `Resets ${formatPeriodResetDate(usage.selected_period)}`
      : formatPeriodLabel(usage.selected_period),
  ].filter((detail) => detail !== null);

  return (
    <section className="rounded-md border border-border-light bg-white p-4">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm font-semibold text-foreground">
          {liveBalances ? "Voice credits" : "Voice credits used"}
        </p>
        <p className="shrink-0 text-sm font-semibold text-foreground tabular-nums">
          {liveBalances
            ? `${formatWholeNumber(liveBalances.remaining_credits)} left`
            : formatWholeNumber(usage.credits_used)}
        </p>
      </div>

      {barCapacity !== null ? (
        <UsageBar
          className="mt-3 w-full"
          isUncovered={isUncovered}
          percentage={(usage.credits_used / barCapacity) * 100}
        />
      ) : null}

      <div className="mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <p>
          {details.map((detail, index) => (
            <Fragment key={detail}>
              {index > 0 ? <span aria-hidden="true"> · </span> : null}
              {detail}
            </Fragment>
          ))}
        </p>
        <p className="text-right">
          {isCurrentPeriod
            ? projectedCredits !== null
              ? `Projected: ${formatWholeNumber(projectedCredits)} used by reset`
              : null
            : "Period ended"}
        </p>
      </div>
    </section>
  );
};

/**
 * Voice usage with no pack to draw on. Caution rather than warning: nothing
 * is cut off, but every credit is billable and sales is the only way to
 * change that.
 */
const NoVoicePackBanner: FC = () => (
  <InfoBanner
    title="Voice agent usage is accruing without a voice credit pack"
    description={
      <>
        To add credits, contact{" "}
        <Link href="mailto:sales@alpharun.com">sales@alpharun.com</Link>.
      </>
    }
    variant="CAUTION"
  />
);

interface UsageByAgentProps {
  agents: readonly VoiceCreditUsageByAgent[];
  isLoading: boolean;
}

const UsageByAgent: FC<UsageByAgentProps> = ({ agents, isLoading }) => (
  <section className="space-y-3">
    <h2 className="text-base font-semibold text-foreground">Usage by agent</h2>
    <SettingsTable>
      <SettingsTableHeaderSection>
        <tr>
          <SettingsTableHeaderCell className="w-[70%]">
            Voice agent
          </SettingsTableHeaderCell>
          <SettingsTableHeaderCell className="w-[30%] text-right">
            Voice credits used
          </SettingsTableHeaderCell>
        </tr>
      </SettingsTableHeaderSection>
      <SettingsTableBodySection>
        {isLoading ? <TableSkeleton /> : null}
        {agents.map((agent) => (
          <tr className="hover:bg-gray-50" key={agent.voice_agent_id}>
            <SettingsTableDataCell>
              {agent.voice_agent_name || "Unnamed agent"}
            </SettingsTableDataCell>
            <SettingsTableDataCell className="text-right tabular-nums">
              {formatWholeNumber(agent.credits_used)}
            </SettingsTableDataCell>
          </tr>
        ))}
      </SettingsTableBodySection>
    </SettingsTable>
  </section>
);
